import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import H4 from "../../atoms/typography/H4"
import { theme } from "../../theme"
import styled from "styled-components"
import H1 from "../../atoms/typography/H1"
import H3 from "../../atoms/typography/H3"
import H2 from "../../atoms/typography/H2"
import Body from "../../atoms/typography/Body"
import BodyLarge from "../../atoms/typography/BodyLarge"
//@ts-ignore
import ItvLogo from "../../atoms/icons/images/itvlogo.svg"
//@ts-ignore
import UnchrLogo from "../../atoms/icons/images/unchrlogo.svg"
//@ts-ignore
import CigLogo from "../../atoms/icons/images/ciglogo.svg"
//@ts-ignore
import CbsLogo from "../../atoms/icons/images/cbslogo.svg"
//@ts-ignore
import FnLogo from "../../atoms/icons/images/fnlogo.svg"
import { ContactPhoneIcon } from "../../atoms/icons/ContactPhoneIcon"
import { ArrowRight } from "../../atoms/icons/ArrowRight"
import { Link } from "react-router-dom"
import NavigationTextButton from "../../atoms/buttons/NavigationTextButton"
import { MediaQuery } from "../../../utils/design-helpers"
import SmallColorfulButton from "../../atoms/buttons/SmallColorfulButton"
import Markdown from "react-markdown"
import { RequestDemoButton } from "../../molecules/store/RequestDemo"
import UspCarouselInstant from "../../molecules/store/ProductPages/UspCarouselInstant"
import BookRentalPage from "./BookRental"
import CustomizeRentalAndOrder from "../store/rentals/CustomizeRentalAndOrder"
import { CartStructure } from "../../interfaces/SaleorTypes"
import dayjs from "dayjs"
import { DownloadProductSheetButton } from "../../molecules/store/DownloadProductSheet"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { useProductPageCollection } from "../../../hooks/products/useProductPageCollection"

const ColoredTitle = styled(H1)`
  background: ${(props) => props.theme.gradients.yellow};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`

const HeroContent = styled.div`
  position: absolute;
  top: 0;
  left: -20%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;

  ${MediaQuery.XS} {
    left: 0;
  }
`
const GreySection = styled.div`
  width: 100%;
  background: var(--Darker-grey-gradient, linear-gradient(180deg, #585858 0%, #444 100%));
`

const TerminalImage = styled.img`
  width: 100%;
  height: 560px;
  object-fit: cover;
`

const FlexRowCol = styled.div`
  display: flex;
  flex-direction: column;
  padding: 44px 16px 44px 16px;
  justify-content: space-between;
  max-width: 80%;

  ${MediaQuery.XS} {
    max-width: 100%;
  }
`

const FooterImage = styled.img`
  width: 100%;
  height: 259px;
  object-fit: cover;
`

const FooterContainer = styled.div`
  position: absolute;
  top: 25%;
  left: 0;
  padding: 1rem;
  width: 100%;
`

const ContactSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;

  :hover {
    filter: opacity(0.6);
  }
`

const FixedBottomMenu = styled.div`
  width: 100%;
  padding: 0.5rem;
  background-color: rgba(250, 250, 250, 0.8);
  backdrop-filter: blur(3px);
  position: sticky;
  left: 0;
  top: 75px;
  z-index: 4;
  box-shadow: 0px 1px 6px 0px rgba(119, 119, 119, 0.25);

  ${MediaQuery.XS} {
    top: 55px;
  }

  ${MediaQuery.SM}, ${MediaQuery.MD} {
    top: 50px;
  }

  ${MediaQuery.LG} {
    top: 60px;
  }
`

const MenuNavigationContainer = styled.div`
  display: flex;
  justify-content: space-around;
  flex: 0.5;
  align-items: center;
`

const MapCoverageImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding-left: 48px;

  ${MediaQuery.XS} {
    height: 162px;
  }
`

const ScaledPartners = styled.div`
  transform: scale(0.7);
`

const RentalProductPage: React.FC = () => {
  const { productPages, error } = useProductPageCollection()
  const kuproductpage = productPages?.items?.find((x: any) => x.title === "ku")
  const [showRentalCheckout, setShowRentalCheckout] = React.useState(false)
  const [rentalOrder, setRentalOrder] = React.useState<CartStructure[] | undefined>(undefined)
  const [selectedDate, setSelectedDate] = React.useState(dayjs().add(1, "week").format("YYYY-MM-DD"))

  if (error) {
    captureExceptionWithContext(error, "error fetching product pages from contentful")
  }

  const overviewRef = React.useRef<HTMLDivElement>(null)
  const howItWorksRef = React.useRef<HTMLDivElement>(null)
  const applicationsRef = React.useRef<HTMLDivElement>(null)
  const bookdemoRef = React.useRef<HTMLDivElement>(null)
  const videoRef = React.useRef<HTMLVideoElement>(null)

  const [activeSection, setActiveSection] = React.useState(0)

  React.useEffect(() => {
    document.title = "Rental Product"

    // Function to handle scrolling
    const handleScroll = () => {
      const scrollPosition = window.scrollY

      const overviewOffset = overviewRef.current?.offsetTop || 0
      const applicationsOffset = howItWorksRef.current?.offsetTop || 0
      const specOffset = applicationsRef.current?.offsetTop || 0
      const bookDemoOffset = bookdemoRef.current?.offsetTop || 0

      if (scrollPosition >= overviewOffset && scrollPosition < applicationsOffset) {
        setActiveSection(0)
      } else if (scrollPosition >= applicationsOffset && scrollPosition < specOffset) {
        setActiveSection(1)
      } else if (scrollPosition >= specOffset && scrollPosition < bookDemoOffset) {
        setActiveSection(2)
      }
    }

    const options = {
      root: null, // Use the viewport as the root
      rootMargin: "0px", // No margin around the viewport
      threshold: 0.5, // Trigger when half of the video is visible
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && videoRef.current) {
          videoRef.current.play() // Autoplay when video is in viewport
        } else {
          videoRef.current?.pause() // Pause if not in viewport
        }
      })
    }, options)

    // Observe the video element
    if (videoRef.current) {
      observer.observe(videoRef.current)
    }

    // Set up scroll event listener
    window.addEventListener("scroll", handleScroll)

    // Clean up on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll)
      observer.disconnect()
    }
  }, [])

  const scrollToBookDemo = () => {
    bookdemoRef.current?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" })
  }
  const scrollToOverview = () => {
    overviewRef.current?.scrollIntoView({ behavior: "smooth" })
  }
  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: "smooth" })
  }
  const scrollToAppliccations = () => {
    applicationsRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  if (showRentalCheckout && rentalOrder) {
    return (
      <BookRentalPage
        rentalOrder={rentalOrder}
        selectedDate={selectedDate}
        onClickBack={() => setShowRentalCheckout(false)}
      />
    )
  }

  return (
    <div style={{ position: "relative" }}>
      <FixedBottomMenu>
        <Container>
          <Row nogutter justify="between" align="center">
            <H2 color={theme.colors.dark}>Satcube Instant</H2>
            <Hidden xs sm md>
              <MenuNavigationContainer>
                <NavigationTextButton
                  title="Overview"
                  onClick={scrollToOverview}
                  active={activeSection === 0}
                  color={theme.colors.dark}
                  subMenu
                />
                <Spacer spacing={1} />
                <NavigationTextButton
                  title="How it works"
                  onClick={scrollToHowItWorks}
                  active={activeSection === 1}
                  color={theme.colors.dark}
                  subMenu
                />
                <Spacer spacing={1} />
                <NavigationTextButton
                  title="Applications"
                  onClick={scrollToAppliccations}
                  active={activeSection === 2}
                  color={theme.colors.dark}
                  subMenu
                />
              </MenuNavigationContainer>
            </Hidden>
            <SmallColorfulButton
              title="Book rental"
              variant="yellow"
              iconRight={<ArrowRight color={theme.colors.greyDark} />}
              onClick={scrollToBookDemo}
            />
          </Row>
        </Container>
      </FixedBottomMenu>
      <div style={{ position: "relative" }}>
        <video
          src={
            "https://videos.ctfassets.net/kzqg847wz5jr/6s0vHObvR9Ee6txVtRczIM/c74bd2c75580d02ddeb88bacbf2c0984/rental-hero.webm"
          }
          poster="https://images.ctfassets.net/kzqg847wz5jr/54Ntk5lpuQ6qO6UPRWtVJs/01118e74cb4658eb22a5979df7d7c3d8/Rentals_header.webp"
          style={{
            marginBottom: -8,
            width: "100%",
            height: "100vh",
            objectFit: "cover",
          }}
          loop
          autoPlay
          muted
          playsInline
        >
          Your browser does not support the video tag.
        </video>
        <HeroContent>
          <Container>
            <ColoredTitle textAlign="center">Satcube Instant</ColoredTitle>
            <HorizontalSpacer spacing={1.4} />
            <div>
              <H4 color="white" textAlign="center">
                Our all-in-one rental service
              </H4>
              <HorizontalSpacer spacing={0.5} />
              <H3 color="white" textAlign="center">
                Seamless satellite broadband — delivered within 2 business days
              </H3>
            </div>
            <HorizontalSpacer spacing={2.5} />
            <Row justify="center">
              <SmallColorfulButton
                variant="yellow"
                title="Book rental"
                iconRight={<ArrowRight color={theme.colors.greyDark} />}
                onClick={scrollToBookDemo}
              />
            </Row>
          </Container>
        </HeroContent>
      </div>
      <div ref={overviewRef} />
      <GreySection>
        <Row>
          <Col lg={6}>
            <TerminalImage src="https://images.ctfassets.net/kzqg847wz5jr/7dlfIl3fvQ1qhYotquayMP/a67731e36322ef47fe6330b28374db6f/scenario-broadcast.webp" />
          </Col>
          <Col lg={6} style={{ display: "flex", alignItems: "center" }}>
            <Container>
              <FlexRowCol>
                <ColoredTitle>Satcom made simple.</ColoredTitle>
                <HorizontalSpacer spacing={1} />
                <div>
                  <Row nogutter justify="between">
                    <H3 color="white">Hassle-free delivery</H3>
                    <H3 color="white">Easy to use</H3>
                    <H3 color="white">Data included</H3>
                  </Row>
                </div>
                <HorizontalSpacer spacing={1} />
                <Body color="white">
                  Satcube Instant is a modern satellite communication service delivering a ready to use Satcube terminal
                  with capacity, wherever you need it in the world, within two business days. Accessories can be
                  tailored to fit your needs. Upon receiving your terminal, a member of our sales team will take care of
                  your onboarding through the communication channel of your choice.
                </Body>
                <HorizontalSpacer spacing={2} />

                <div>
                  <DownloadProductSheetButton productSheetType="satcube_rental" buttonType="coloryellow" />
                </div>
              </FlexRowCol>
            </Container>
          </Col>
        </Row>
      </GreySection>

      <GreySection>
        <HorizontalSpacer spacing={8} />
        <Container>
          <Row align="center" justify="between">
            <Col lg={5}>
              <ColoredTitle>Wherever you are.</ColoredTitle>
              <HorizontalSpacer spacing={1} />
              <Row justify="between" nogutter>
                <H3 color="white">Online management platform</H3>
                <H3 color="white">Global coverage</H3>
              </Row>
              <HorizontalSpacer spacing={1} />

              <Body color="white">
                With your terminal, you get access to an online dashboard enabling you to keep track of your
                terminal(s), see data consumption in real time and top up data if needed. You can choose an initial
                rental period of one to six months and can extend it if necessary.
              </Body>
              <HorizontalSpacer spacing={1} />
              <Body color="white">
                The terminal runs on the Intelsat Flexmove network, providing global and fast coverage for all your
                connectivity needs.
              </Body>
            </Col>
            <Col lg={7}>
              <MapCoverageImage src="https://images.ctfassets.net/kzqg847wz5jr/7ymVFoQMxqSUlg8sVFJwyW/f095fcce638a82b7921621a3ed38c570/Service_Area_-_FlexMove_Commercial_-_edited-1_2.png" />
            </Col>
          </Row>
          <HorizontalSpacer spacing={8} />
        </Container>
      </GreySection>
      <div ref={howItWorksRef} />
      <Container>
        <HorizontalSpacer spacing={8} />
        <UspCarouselInstant color={theme.gradients.yellow} />
        <HorizontalSpacer spacing={8} />
      </Container>

      <div ref={applicationsRef} />
      <Container>
        <Row justify="center">
          <video
            ref={videoRef}
            src={
              "https://videos.ctfassets.net/kzqg847wz5jr/18EOhlCtsrs1IhJ95mx90A/add119b2770519741290d089044bd645/itv-720p__1_.webm"
            }
            poster="https://images.ctfassets.net/kzqg847wz5jr/6GHc6lVYeBBJzKdSIEo5jg/5f6564c3d3ff870aa027bb80a69fe933/Rentals_itv.webp"
            style={{
              marginBottom: -8,
              width: "100%",
              height: "70vh",
              objectFit: "cover",
              borderRadius: 20,
            }}
            muted
            playsInline
            controls
          />
        </Row>
      </Container>

      <HorizontalSpacer spacing={4} />
      <Container>
        <Row gutterWidth={48}>
          <Col lg={6}>
            <Row justify="between" align="center">
              <UnchrLogo />
              <CigLogo />
              <ScaledPartners>
                <FnLogo />
              </ScaledPartners>
            </Row>
            <Row justify="around" align="center">
              <ScaledPartners>
                <CbsLogo />
              </ScaledPartners>
              <ScaledPartners>
                <ItvLogo />
              </ScaledPartners>
            </Row>
          </Col>
          <Col lg={6}>
            <H2>Some of the world’s greatest Media and Humanitarian organizations trust Satcube</H2>
            <HorizontalSpacer spacing={1} />
            <Body>
              Media companies and Humanitarian Aid organizations use our Satcube Instant rental service when critical
              communications need arise as it is a portable, pre-configured and easy-to-use solution for quick
              deployment in breaking news situations or in the case of sudden humanitarian emergencies. Our terminals
              are compatible with encoders such as LiveU, Dejero, Aviwest or TVU, they are easy to set up and create a
              satellite broadband connection in less than 60 seconds. The Satcube terminal also comes in an all-in-one
              carry-on bag, perfect for air, rail and car travel at only 8kg.  
            </Body>
          </Col>
        </Row>
      </Container>
      <HorizontalSpacer spacing={4} />

      <HorizontalSpacer spacing={1.5} />
      <Row justify="center">
        <div style={{ maxWidth: 680 }}>
          <BodyLarge color={theme.colors.dark} textAlign="center">
            <Markdown>{kuproductpage?.quote}</Markdown>
          </BodyLarge>
        </div>
        <div ref={bookdemoRef} />
      </Row>

      <HorizontalSpacer spacing={5} />
      <CustomizeRentalAndOrder
        setShowRentalCheckout={setShowRentalCheckout}
        setRentalOrder={setRentalOrder}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />
      <HorizontalSpacer spacing={5} />

      <div style={{ position: "relative", width: "100%" }}>
        <FooterImage src="https://images.ctfassets.net/kzqg847wz5jr/1L91M1LdJ0rM8WwWJXNIhw/2e7c2ef2ada5126dfc880dcc82c49746/MicrosoftTeams-image__8_.png" />
        <FooterContainer>
          <Container>
            <Row justify="between">
              <div>
                <H1 color="white">Want to know more?</H1>
                <RequestDemoButton productType="satcube_ku" buttonType="textwhite" />
              </div>
              <Link to="/contact">
                <ContactSection>
                  <ContactPhoneIcon color="white" />
                  <Spacer spacing={1} />
                  <H3 color="white">Contact sales</H3>
                </ContactSection>
              </Link>
            </Row>
          </Container>
        </FooterContainer>
      </div>
    </div>
  )
}

export default RentalProductPage
